import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { catchError, EMPTY, finalize } from 'rxjs';
import { Logger, SharedModule } from '@aiwyn/shared';
import { AuthService } from '@aiwyn/auth';
import { Avatar, getAvatarByFullName } from '../../../../../../shared/components/avatar/avatar.component';
import { AppState } from '../../../../../../state/app.state';
import { Comment } from '../../../../../client-requests/components/comments/comments.model';
import { CxpRelativeTimePipe } from '../../../../shared';
import { LoadItemComments, MarkItemCommentsViewed, PostItemComment } from '../../../../state/cxp-crl-detail.actions';
import { CxpCrlDetailSelectors } from '../../../../state/cxp-crl-detail-state.selectors';
import { CxpCrlSubscriptionsStateSelectors } from '../../../../state/cxp-crl-subscriptions-state.selectors';
import { CrlCommentDwellDirective } from './crl-comment-dwell.directive';

/**
 * The window comments whose dwell elapses together are collected over before one
 * write is sent. Zero: it is a batching tick, not a wait — a screenful that
 * becomes visible at once arms its dwell timers in the same tick and so lands in
 * the same batch.
 */
const CRL_COMMENT_VIEWED_BATCH_MS = 0;

/**
 * Comments tab of the CRL item-detail overlay — an ALWAYS-ON, un-gated thread
 * (the `ENABLE_CLIENT_REQUEST_THREADED_COMMENTS` flag was removed in the
 * redesign). Renders the real threaded messages for the selected request item
 * and lets the user add a top-level comment or reply.
 *
 * The thread itself lives in `CxpCrlDetailState`, cached per item id so returning
 * to an item shows the previously-loaded comments instantly while a fresh load
 * runs (stale-while-revalidate). This component reads that cache via
 * `getSelectedItemComments` and drives it with `LoadItemComments` /
 * `PostItemComment`; only UI-interaction state (the reply box, the composer
 * fields, the in-flight posting flag) stays local, and all of it is reset when
 * `itemId` is rebound because the panel reuses this instance across prev/next
 * navigation. Replies render inline under
 * their parent rather than in a thread drawer (legacy UX), matching the redesign.
 */
@Component({
  selector: 'cxp-crl-panel-comments',
  templateUrl: './crl-panel-comments.component.html',
  styleUrls: ['./crl-panel-comments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, ReactiveFormsModule, CxpRelativeTimePipe, CrlCommentDwellDirective],
})
export class CrlPanelCommentsComponent {
  /** The request-item id the comments thread is scoped to (the message entity id). */
  readonly itemId = input.required<number>();
  /**
   * Restricted-visibility audit view: for a masked restricted item the thread is
   * NOT fetched (no leak over the network — the backend does not mask comments)
   * and both the composer and per-comment reply controls are hidden. A neutral
   * restricted placeholder is shown in place of the thread instead.
   */
  readonly isAuditView = input<boolean>(false);

  private readonly store = inject(Store);
  private readonly authService = inject(AuthService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);

  private readonly commentsView = this.store.selectSignal(CxpCrlDetailSelectors.getSelectedItemComments);
  protected readonly comments = computed(() => this.commentsView().comments);
  protected readonly isLoading = computed(() => this.commentsView().isLoading);
  protected readonly isPosting = signal<boolean>(false);

  /** Id of the comment whose inline reply box is open, or null when none is. */
  protected readonly replyingToId = signal<string | null>(null);

  protected readonly newCommentControl = new FormControl<string>('', { nonNullable: true });
  protected readonly replyControl = new FormControl<string>('', { nonNullable: true });

  protected readonly hasComments = computed(() => this.comments().length > 0);

  /** The viewer's own linked contact ids — a user holds more than one. */
  private readonly viewerContactIds = this.store.selectSignal(AppState.getUserContactIds);

  /**
   * Ids of the comments (top level and replies) written by someone other than
   * the viewer — the only ones a view is worth recording for.
   *
   * Deliberately NOT the thread's own `isUnread`: that is computed against a
   * SINGLE contact id (`getContactId()`), while a user holds several, so it can
   * report a comment as already-read and suppress the write forever. Authorship
   * is the part this component can establish for itself, and re-reporting an
   * already-viewed comment is free — the insert carries `on_conflict` with no
   * update columns, so a repeat writes nothing.
   */
  private readonly markableCommentIds = computed(() => collectOthersIds(this.comments(), new Set(this.viewerContactIds() ?? [])));

  /**
   * Comments reported as viewed since the thread loaded. The dot clears from
   * here rather than waiting for a reload: marking viewed does not refetch, so
   * the loaded thread keeps reporting them unread for the rest of the session.
   */
  private readonly reportedViewedIds = signal<ReadonlySet<string>>(new Set());

  /**
   * Comments still to show the unread dot: someone else's, still unread on the
   * loaded thread, and not yet reported viewed from this screen.
   *
   * Authorship is re-checked rather than trusting `isUnread` alone — that flag is
   * computed against a SINGLE contact id, so a comment written by another of the
   * viewer's OWN contacts reads as someone else's and would keep a dot forever,
   * since {@link markableCommentIds} correctly never reports it viewed.
   */
  protected readonly unreadCommentIds = computed(() => {
    const reported = this.reportedViewedIds();
    const markable = this.markableCommentIds();
    const ids = new Set<string>();
    for (const id of collectUnreadIds(this.comments())) {
      if (markable.has(id) && !reported.has(id)) ids.add(id);
    }
    return ids as ReadonlySet<string>;
  });

  private readonly commentIds = this.store.selectSignal(CxpCrlSubscriptionsStateSelectors.getItemCommentIds);

  /** Highest message id already rendered, replies included. */
  private readonly loadedMessageIdCeiling = computed(() => maxMessageId(this.comments()));

  /**
   * Id this component last asked to load. Without it a reload that cannot
   * produce the message — anything the thread query does not return — would be
   * re-dispatched on every emission.
   */
  private lastRequestedMessageId = 0;

  /**
   * Pulls a comment in as it lands, rather than leaving the reader to reopen the
   * panel. The live query reports each item's NEWEST message id; an id above
   * everything on screen is a comment this thread has not got yet.
   *
   * The ceiling comparison is also the guard against reloading our own post: by
   * the time the subscription reports it, posting has already refreshed the
   * thread, so the id is not above the ceiling and nothing further happens.
   */
  private readonly reloadOnNewMessage = effect(() => {
    const itemId = this.itemId();
    const latest = this.commentIds().latestMessageIdByItem?.[itemId] ?? 0;
    // An audit view deliberately never fetches the thread, so its ceiling stays
    // at zero — reloading on it would neither help nor ever settle.
    if (this.isAuditView() || latest === 0) return;
    if (latest <= this.loadedMessageIdCeiling() || latest === this.lastRequestedMessageId) return;

    this.lastRequestedMessageId = latest;
    this.store.dispatch(new LoadItemComments(itemId));
  });

  /** Comments that have dwelled on screen and are waiting for the next batched write. */
  private readonly pendingViewedIds = new Set<string>();
  private viewedFlushTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Item the in-flight post belongs to, dropped whenever the panel rebinds
   * `itemId`. The panel reuses this instance across prev/next, so a post that
   * resolves after the user moved on must not touch the item now on screen.
   */
  private postingItemId: number | null = null;

  /** Composer's leading avatar — the signed-in user (stable for the overlay's life). */
  protected readonly currentUserAvatar = this.buildCurrentUserAvatar();

  constructor() {
    this.destroyRef.onDestroy(() => this.cancelViewedFlush());

    effect(() => {
      const itemId = this.itemId();
      this.replyingToId.set(null);
      this.newCommentControl.reset();
      this.replyControl.reset();
      // Anything still waiting to be reported belongs to the item being left.
      this.cancelViewedFlush();
      this.pendingViewedIds.clear();
      this.reportedViewedIds.set(new Set());
      this.lastRequestedMessageId = 0;
      // Prev/next rebinds `itemId` on the SAME instance, so release any in-flight
      // post's claim on the composer: it must never sit disabled waiting on a post
      // that belongs to the item the user just left. An unsent draft is discarded
      // with it, matching the questionnaire's per-item reseed.
      this.postingItemId = null;
      this.isPosting.set(false);
      // Audit view (masked restricted item): never fetch the thread — the backend
      // does not mask comments, so loading it would leak private content over the
      // network. The template shows a restricted placeholder instead.
      if (this.isAuditView()) return;
      // Cached thread (if any) renders immediately; this refreshes it.
      this.store.dispatch(new LoadItemComments(itemId));
    });
  }

  /** Avatar payload for the reusable `app-avatar` (needs a `fullName`). */
  protected avatarFor(author: Comment['author']): Avatar {
    return {
      id: author.id,
      firstName: author.firstName,
      lastName: author.lastName,
      fullName: `${author.firstName} ${author.lastName}`.trim(),
    };
  }

  private buildCurrentUserAvatar(): Avatar {
    const user = this.authService.getUserSnapshot();
    const id = user?.getContactId() ?? 0;
    const displayName = user?.getDisplayName()?.trim() || 'You';
    return getAvatarByFullName(id, displayName) ?? this.avatarFor({ id: '0', firstName: 'You', lastName: '' });
  }

  protected onAddComment(): void {
    const text = this.newCommentControl.value.trim();
    if (!text || this.isPosting()) return;
    this.post(text, null, () => this.newCommentControl.reset());
  }

  protected onStartReply(commentId: string): void {
    this.replyControl.reset();
    this.replyingToId.set(commentId);
  }

  protected onCancelReply(): void {
    this.replyControl.reset();
    this.replyingToId.set(null);
  }

  protected onSubmitReply(parentId: string): void {
    const text = this.replyControl.value.trim();
    if (!text || this.isPosting()) return;
    this.post(text, parentId, () => {
      this.replyControl.reset();
      this.replyingToId.set(null);
    });
  }

  /**
   * A rendered comment has been on screen long enough to count as read. Only
   * other people's comments are worth reporting, and each is queued rather than
   * written immediately so a screenful that becomes visible together costs one
   * call.
   */
  protected onCommentDwelled(commentId: string): void {
    if (!this.markableCommentIds().has(commentId)) return;
    this.pendingViewedIds.add(commentId);
    this.scheduleViewedFlush();
  }

  /**
   * Coalesces every comment whose dwell elapses in the same tick into a single
   * write; the zero delay is the batching window, not a wait.
   */
  private scheduleViewedFlush(): void {
    if (this.viewedFlushTimer !== null) return;
    this.viewedFlushTimer = setTimeout(() => {
      this.viewedFlushTimer = null;
      this.flushViewed();
    }, CRL_COMMENT_VIEWED_BATCH_MS);
  }

  private flushViewed(): void {
    const commentIds = [...this.pendingViewedIds];
    this.pendingViewedIds.clear();
    if (commentIds.length === 0) return;

    this.store
      .dispatch(new MarkItemCommentsViewed(this.itemId(), commentIds))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        complete: () => this.reportedViewedIds.update((seen) => new Set([...seen, ...commentIds])),
        error: (err) => this.logger.error('[CXP CRL Comments] marking viewed failed:', err),
      });
  }

  private cancelViewedFlush(): void {
    if (this.viewedFlushTimer === null) return;
    clearTimeout(this.viewedFlushTimer);
    this.viewedFlushTimer = null;
  }

  private post(text: string, parentId: string | null, onDone: () => void): void {
    const itemId = this.itemId();
    this.postingItemId = itemId;
    this.isPosting.set(true);
    // The state handler posts then reloads the item's cached thread; the dispatch
    // observable completes once that reload lands, so the composer clears only
    // after the new comment is in the cache. Both callbacks bail once this post is
    // no longer the composer's — the item on screen owns its own state.
    this.store
      .dispatch(new PostItemComment(itemId, text, parentId))
      .pipe(
        finalize(() => {
          if (this.postingItemId !== itemId) return;
          this.postingItemId = null;
          this.isPosting.set(false);
        }),
        catchError((err) => {
          this.logger.error('[CXP CRL Comments] post failed:', err);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.postingItemId !== itemId) return;
        onDone();
      });
  }
}

/** Ids the loaded thread still reports as unread — a reply counts on its own terms. */
function collectUnreadIds(comments: Comment[]): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const comment of comments) {
    if (comment.isUnread) ids.add(comment.id);
    for (const reply of comment.replies ?? []) {
      if (reply.isUnread) ids.add(reply.id);
    }
  }
  return ids;
}

/**
 * Ids across the thread whose author is not the viewer — a reply counts on its
 * own terms, like any comment.
 *
 * `author.id` carries the staff id when `isStaff`, otherwise the contact id
 * (`createdByStaffId || createdByContactId`), so staff comments are always
 * someone else's and contact comments are matched against the viewer's own set.
 */
function collectOthersIds(comments: Comment[], viewerContactIds: ReadonlySet<number>): ReadonlySet<string> {
  const ids = new Set<string>();
  const isSomeoneElse = (comment: Comment): boolean =>
    comment.author.isStaff === true || !viewerContactIds.has(Number(comment.author.id));

  for (const comment of comments) {
    if (isSomeoneElse(comment)) ids.add(comment.id);
    for (const reply of comment.replies ?? []) {
      if (isSomeoneElse(reply)) ids.add(reply.id);
    }
  }
  return ids;
}


/** Highest numeric message id across the thread, replies included; 0 when empty. */
function maxMessageId(comments: Comment[]): number {
  let max = 0;
  const consider = (id: string): void => {
    const value = Number(id);
    if (Number.isFinite(value) && value > max) max = value;
  };
  for (const comment of comments) {
    consider(comment.id);
    for (const reply of comment.replies ?? []) consider(reply.id);
  }
  return max;
}
